import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NewsletterService } from '../../../services/newsletter.service';

@Component({
  selector: 'app-newsletter-editor',
  templateUrl: './newsletter-editor.component.html',
  styleUrls: ['./newsletter-editor.component.scss']
})
export class NewsletterEditorComponent implements OnInit {

  campaignId: number | null = null;
  isEditing: boolean = false;

  // Formulario
  name: string = '';
  subject: string = '';
  htmlContent: string = '';
  templateId: number | null = null;
  segmentType: string = 'ALL';
  segmentValue: string = '';

  // Datos de apoyo
  templates: any[] = [];
  previewHtml: string = '';
  audiencePreview: any = null;

  // UI
  loading: boolean = false;
  saving: boolean = false;
  sendingTest: boolean = false;
  testEmail: string = '';
  scheduledAt: string = '';
  errorMessage: string = '';

  // Tipos de usuario (user_types)
  userTypes = [
    { id: 1, name: 'Administrador' },
    { id: 2, name: 'Afiliado' },
    { id: 3, name: 'Cliente' },
    { id: 4, name: 'Producción' },
    { id: 5, name: 'Comercial' }
  ];

  // Config de Quill
  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  constructor(
    private newsletterService: NewsletterService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadTemplates();

    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.campaignId = Number(idParam);
      this.isEditing = true;
      this.loadCampaign(this.campaignId);
    }
  }

  loadTemplates(): void {
    this.newsletterService.getTemplates().subscribe({
      next: (data) => this.templates = data || [],
      error: (err) => console.error('Error cargando plantillas', err)
    });
  }

  loadCampaign(id: number): void {
    this.loading = true;
    this.newsletterService.getCampaign(id).subscribe({
      next: (c) => {
        this.name = c.name;
        this.subject = c.subject;
        this.htmlContent = c.htmlContent;
        this.templateId = c.templateId;
        this.segmentType = c.segmentType;
        this.segmentValue = c.segmentValue || '';
        this.loading = false;
        this.updatePreview();
      },
      error: () => {
        this.errorMessage = 'No se pudo cargar la campaña.';
        this.loading = false;
      }
    });
  }

  onTemplateChange(): void {
    if (!this.templateId) return;

    const selected = this.templates.find(t => t.id === this.templateId);
    if (!selected) return;

    if (this.htmlContent && this.htmlContent.trim() !== '' &&
        !confirm('Si cambias la plantilla se sobrescribirá el contenido actual. ¿Continuar?')) {
      this.templateId = null;
      return;
    }

    this.subject = selected.subject;
    this.htmlContent = selected.htmlContent;
    this.updatePreview();
  }

  updatePreview(): void {
    if (!this.htmlContent) {
      this.previewHtml = '';
      return;
    }
    this.newsletterService.previewHtml(this.htmlContent, 'Ejemplo Usuario', 'ejemplo@correo.com')
      .subscribe({
        next: (res) => this.previewHtml = res.rendered,
        error: () => this.previewHtml = this.htmlContent
      });
  }

  onHtmlChange(): void {
    this.updatePreview();
  }

  previewAudience(): void {
    this.newsletterService.previewAudience(this.segmentType, this.segmentValue).subscribe({
      next: (data) => this.audiencePreview = data,
      error: (err) => alert('Error: ' + (err.error || 'No se pudo calcular la audiencia'))
    });
  }

  validate(): boolean {
    this.errorMessage = '';
    if (!this.name.trim()) { this.errorMessage = 'El nombre es obligatorio.'; return false; }
    if (!this.subject.trim()) { this.errorMessage = 'El asunto es obligatorio.'; return false; }
    if (!this.htmlContent.trim()) { this.errorMessage = 'El contenido no puede estar vacío.'; return false; }
    if (this.segmentType === 'BY_ROLE' && !this.segmentValue) {
      this.errorMessage = 'Selecciona un rol.';
      return false;
    }
    if (this.segmentType === 'MANUAL_LIST' && !this.segmentValue.trim()) {
      this.errorMessage = 'Ingresa al menos un email en la lista.';
      return false;
    }
    return true;
  }

  buildPayload(): any {
    return {
      name: this.name.trim(),
      subject: this.subject.trim(),
      htmlContent: this.htmlContent,
      templateId: this.templateId,
      segmentType: this.segmentType,
      segmentValue: this.segmentValue
    };
  }

  save(): void {
    if (!this.validate()) return;
    this.saving = true;

    const payload = this.buildPayload();
    const obs = this.isEditing
      ? this.newsletterService.updateCampaign(this.campaignId!, payload)
      : this.newsletterService.createCampaign(payload);

    obs.subscribe({
      next: (c) => {
        this.saving = false;
        this.campaignId = c.id;
        this.isEditing = true;
        alert('Campaña guardada como borrador.');
        this.router.navigate(['/newsletter']);
      },
      error: (err) => {
        this.saving = false;
        this.errorMessage = err.error || 'Error al guardar.';
      }
    });
  }

  sendTest(): void {
    if (!this.testEmail || !this.testEmail.includes('@')) {
      alert('Ingresa un email válido para la prueba.');
      return;
    }

    if (!this.campaignId) {
      if (!this.validate()) return;
      if (!confirm('Primero hay que guardar la campaña como borrador. ¿Continuar?')) return;

      this.newsletterService.createCampaign(this.buildPayload()).subscribe({
        next: (c) => {
          this.campaignId = c.id;
          this.isEditing = true;
          this.doSendTest();
        },
        error: (err) => alert('Error al crear borrador: ' + (err.error || 'Error'))
      });
    } else {
      this.doSendTest();
    }
  }

  private doSendTest(): void {
    this.sendingTest = true;
    this.newsletterService.sendTest(this.campaignId!, this.testEmail).subscribe({
      next: () => {
        this.sendingTest = false;
        alert('Correo de prueba enviado a ' + this.testEmail);
      },
      error: (err) => {
        this.sendingTest = false;
        alert('Error enviando prueba: ' + (err.error || 'Error'));
      }
    });
  }

  sendNow(): void {
    if (!this.validate()) return;
    if (!confirm('¿Enviar esta campaña ahora a todos los destinatarios?')) return;

    const proceed = (id: number) => {
      this.newsletterService.sendCampaign(id).subscribe({
        next: () => {
          alert('Envío iniciado. Puedes ver el progreso en el detalle de la campaña.');
          this.router.navigate(['/newsletter', id]);
        },
        error: (err) => alert('Error al enviar: ' + (err.error || 'Error'))
      });
    };

    if (!this.campaignId) {
      this.newsletterService.createCampaign(this.buildPayload()).subscribe({
        next: (c) => proceed(c.id),
        error: (err) => alert('Error: ' + (err.error || 'Error'))
      });
    } else {
      this.newsletterService.updateCampaign(this.campaignId, this.buildPayload()).subscribe({
        next: () => proceed(this.campaignId!),
        error: (err) => alert('Error: ' + (err.error || 'Error'))
      });
    }
  }

  schedule(): void {
    if (!this.validate()) return;
    if (!this.scheduledAt) {
      alert('Selecciona fecha y hora.');
      return;
    }

    const proceed = (id: number) => {
      this.newsletterService.scheduleCampaign(id, this.scheduledAt).subscribe({
        next: () => {
          alert('Campaña programada.');
          this.router.navigate(['/newsletter']);
        },
        error: (err) => alert('Error: ' + (err.error || 'Error'))
      });
    };

    if (!this.campaignId) {
      this.newsletterService.createCampaign(this.buildPayload()).subscribe({
        next: (c) => proceed(c.id),
        error: (err) => alert('Error: ' + (err.error || 'Error'))
      });
    } else {
      this.newsletterService.updateCampaign(this.campaignId, this.buildPayload()).subscribe({
        next: () => proceed(this.campaignId!),
        error: (err) => alert('Error: ' + (err.error || 'Error'))
      });
    }
  }

  cancel(): void {
    this.router.navigate(['/newsletter']);
  }
}






